import { createTheme } from '@mui/material/styles';
import { colors, typography, borderRadius, shadows } from './styles/constants';

/**
 * Enhanced theme for the wedding website
 * This theme includes more component overrides and custom variants
 */
const theme = createTheme({
  // Color palette
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
  },
  
  // Typography settings
  typography: {
    fontFamily: typography.fontFamilies.primary,
    h1: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.bold,
    },
    h2: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.bold,
    },
    h3: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.semiBold,
    },
    h4: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.semiBold,
    },
    h5: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.regular,
    },
    h6: {
      fontFamily: typography.fontFamilies.primary,
      fontWeight: typography.fontWeights.regular,
    },
    body1: {
      fontFamily: typography.fontFamilies.primary,
    },
    body2: {
      fontFamily: typography.fontFamilies.primary,
    },
    button: {
      fontFamily: typography.fontFamilies.secondary,
      textTransform: 'none',
    },
  },
  
  // Component overrides
  components: {
    // Button overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.button,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: colors.secondary.darkSolid,
          color: '#fff',
          '&:hover': {
            backgroundColor: colors.secondary.darkHover,
          },
        },
        outlined: {
          borderColor: colors.secondary.darkSolid,
          color: colors.secondary.darkSolid,
          '&:hover': {
            borderColor: colors.secondary.darkHover,
            backgroundColor: 'rgba(175, 99, 42, 0.04)',
          },
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: colors.primary.main,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: colors.secondary.darkSolid,
            '&:hover': {
              backgroundColor: colors.secondary.darkHover,
            },
          },
        },
      ],
    },
    
    // Card overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.card,
          boxShadow: shadows.card,
          backgroundColor: colors.background.card,
        },
      },
    },
    
    // Typography overrides
    MuiTypography: {
      styleOverrides: {
        root: {
          marginBottom: 8,
        },
      },
      variants: [
        {
          props: { variant: 'h1' },
          style: {
            fontSize: '3.5rem',
            '@media (max-width:600px)': {
              fontSize: '2.5rem',
            },
          },
        },
        {
          props: { variant: 'h2' },
          style: {
            fontSize: '3rem',
            '@media (max-width:600px)': {
              fontSize: '2.25rem',
            },
          },
        },
      ],
    },
    
    // Container overrides
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          '@media (min-width:600px)': {
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
    },
    
    // TextField overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: 16,
        },
      },
    },
    
    // Radio overrides
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.secondary.darkSolid,
          '&.Mui-checked': {
            color: colors.secondary.darkSolid,
          },
        },
      },
    },
    
    // Divider overrides
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '16px 0',
        },
      },
    },
  },
});

export default theme;
